export type FormState = {
  email?: string;
  password?: string;
  newPassword?: string;
  confirmPassword?: string;
  oldPassword?: string;
  firstName?: string;
  lastName?: string;
  jobTitle?: string;
  companyName?: string;
};

const PASSWORD_REQUIREMENTS = [
  {
    validator: (password: string) => password?.length >= 8,
    message: 'At least 8 characters',
  },
  {
    validator: (password: string) => password?.match(/(?=.*[A-Z])/),
    message: 'At least 1 uppercase letter',
  },
  {
    validator: (password: string) => password?.match(/(?=.*[a-z])/),
    message: 'At least 1 lowercase letter',
  },
  {
    validator: (password: string) => password?.match(/(?=.*[0-9])/),
    message: 'At least 1 number',
  },
  {
    validator: (password: string) => password?.match(/^\S*$/),
    message: 'No spaces',
  },
];

export const validatePassword = (password: string) => {
  const invalidRequirements = PASSWORD_REQUIREMENTS.filter(
    ({ validator }) => !validator(password),
  );
  return invalidRequirements[0]?.message;
};

export const isEmailValid = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const isEmailUnique = (email: string, emails: string[]) => {
  return !emails
    .map(e => e.trim().toLowerCase())
    .includes(email.trim().toLowerCase());
};

export const validateForm = (form: FormState) => {
  const formKeys = Object.keys(form) as Array<keyof typeof form>;

  const validationResults = {
    email: isEmailValid(form.email?.trim() || '') || 'Invalid email address',
    password: !!form.password?.trim() || 'Password is required',
    newPassword:
      PASSWORD_REQUIREMENTS.filter(
        ({ validator }) => !validator(form.newPassword || ''),
      )[0]?.message || true,
    confirmPassword:
      form.password === form.confirmPassword ||
      form.newPassword === form.confirmPassword ||
      'Passwords do not match',
    oldPassword: !!form.oldPassword,
    firstName: !!form.firstName?.trim() || 'First name is required',
    lastName: !!form.lastName?.trim() || 'Last name is required',
    jobTitle: !!form.jobTitle?.trim() || 'Job title is required',
    companyName: !!form.companyName?.trim() || 'Company name is required',
  } as const;

  return formKeys.reduce(
    (acc: Record<keyof typeof form, string | boolean>, key) => ({
      ...acc,
      ...(validationResults[key] === true
        ? {}
        : { [key]: validationResults[key] }),
    }),
    {} as Record<keyof typeof form, string | boolean>,
  ) as Record<keyof typeof form, string>;
};

export const isFormValid = (form: FormState) => {
  const res = Object.values(validateForm(form));
  return res.length === 0;
};
